import React, { memo, useMemo, useEffect } from 'react';
import { Form, Input } from 'antd';
import { get } from 'lodash';
import { useTheme } from 'styled-components';
import { UsPgaFlag } from '__generated__/graphql';
import {
  FormItem,
  Select,
  HiddenField,
  Checkbox,
} from '@xbcb/form-item-components';
import { NamePath } from '@xbcb/ui-types';
import { selectFilter, getCodes } from '@xbcb/ui-utils';
import { clearFields } from 'libs/clearFields';
import DisclaimerSelect from 'components/DisclaimerSelect';
import IntendedUseSelect from 'components/IntendedUseSelect';
import PgaDisclaimSection from 'components/PgaDisclaimSection';
// When new form is added or one of these are modified, please make changes in libs/validateUsConsumptionEntry/index.ts
import FdaForm from 'components/FdaForm';
import AphForm from 'components/AphForm';
import TtbForm from 'components/TtbForm';
import NhtForm from 'components/NhtForm';
import EpaForm from 'components/EpaForm';
import CpscForm from 'components/CpscForm';
import { useWorkOrderTaskConfiguration } from 'libs/hooks';
import { intendedUseCodes as intendedUseCodesSelector } from 'selectors';
import { isPgaDisclaimFeatureEnabled } from 'featureFlags';
import { PgaFormBaseProps } from 'types';
import {
  StyledTooltipOption,
  StyledFormInlineDiv,
  StyledFlexDiv,
} from './styles';

export interface LineItemPgaTabProps {
  formDisabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  fullNamePath: NamePath;
  localNamePath: NamePath;
  lineNamePath: NamePath;
  pgaFlag: UsPgaFlag;
}

// TODO Need to add a status hidden field based on the response when submitting a cargo release.
const getProgramCodes = (
  programCode: string | string[],
  pgaFlag: UsPgaFlag,
  program: (p: string) => Record<string, unknown>,
  isDisclaimed: boolean,
) => {
  return typeof programCode === 'string'
    ? [
        {
          label: `${programCode} - ${program(programCode).name}`,
          value: programCode,
        },
      ]
    : programCode
        .filter(
          // Only OFF is allowed for disclaimed NHTSA shipments.
          (code: string) =>
            !(pgaFlag === 'DT1' && isDisclaimed && code !== 'OFF'),
        )
        .map((p: string) => ({
          label: program(p).name as string,
          value: p,
        }));
};

const PgaForm: React.FC<LineItemPgaTabProps> = ({
  localNamePath,
  fullNamePath,
  lineNamePath,
  formDisabled,
  readOnly = false,
  required,
  pgaFlag,
}) => {
  const theme = useTheme();
  const form = Form.useFormInstance();
  const isDisclaimed = Form.useWatch([...fullNamePath, 'disclaim'], form);
  const { readWriteControlsConfig } = useWorkOrderTaskConfiguration();

  const isPGAReadOnly =
    !!readWriteControlsConfig['invoices.products.lines.tariffs.pga']?.readOnly;
  const isPGARequired =
    !!readWriteControlsConfig['invoices.products.lines.tariffs.pga']?.required;

  const agencyCodes = getCodes().CBP.CATAIR.PGA.agency;

  const pgaFlags = getCodes().CBP.CATAIR.PGA.flag;

  const { getFieldValue, setFields } = form;

  const flag = pgaFlags[pgaFlag];

  const { agencyCode, text, disclaimerCodes: filterDisclaimerCodes } = flag;
  const disclaimerCodes = getCodes().CBP.CATAIR.PGA.disclaimer;

  const agency = agencyCodes[agencyCode];

  const program = (code: string) => get(agency, `program.${code}`, {});

  const disclaim = Form.useWatch([...fullNamePath, 'disclaim'], form);
  const mot = Form.useWatch(['conveyance', 'modeOfTransport'], form);

  const isDe3 = pgaFlag === UsPgaFlag.DE3;

  useEffect(() => {
    if (isDe3 && !readOnly) {
      setFields([
        { name: [...fullNamePath, 'disclaim'], value: true },
        { name: [...fullNamePath, 'programCode'], value: 'MAC' },
        { name: [...fullNamePath, 'disclaimerCode'], value: 'A' },
      ]);
    }
  }, [isDe3, readOnly]);

  const intendedUseCode = Form.useWatch([...fullNamePath, 'intendedUse'], form);
  const intendedUseCodes = intendedUseCodesSelector({
    agencyCode: 'APH',
    drugForm: '',
    processingCode: '',
    programCode: '',
  });

  // needs to be a function because we need to evaluate this after
  const getProcessingCodes = () => {
    const programCode = getFieldValue([...fullNamePath, 'programCode']);
    const selectedProgram = program(programCode);
    return Object.entries(get(selectedProgram, 'processing', {})).map(
      ([code, name]: [string, any]) => (
        <StyledTooltipOption key={code} value={code}>
          {name}
        </StyledTooltipOption>
      ),
    );
  };

  const programCodes = useMemo(
    () => getProgramCodes(flag.programCode, pgaFlag, program, isDisclaimed),
    [flag, pgaFlag, program, isDisclaimed],
  );

  const pgaProps: PgaFormBaseProps = {
    pgaFlag,
    disabled: formDisabled,
    readOnly: readOnly || isPGAReadOnly,
    required: required || isPGARequired,
    fullNamePath,
    localNamePath,
  };

  return (
    <>
      <HiddenField
        localNamePath={[...localNamePath, 'agencyCode']}
        value={agencyCode}
        fullNamePath={[...fullNamePath, 'agencyCode']}
        form={form}
      />
      {isPgaDisclaimFeatureEnabled && (
        <PgaDisclaimSection
          lineNamePath={lineNamePath}
          fullNamePath={fullNamePath}
          pgaFlag={pgaFlag}
          agencyName={agencyCode}
        />
      )}
      <StyledFormInlineDiv>
        {agencyCode !== 'CPS' && <p>{text}</p>}
        <StyledFlexDiv>
          <>
            {!(isDisclaimed && agencyCode === 'FDA') &&
              agencyCode !== 'CPS' && (
                <FormItem
                  data-cy="pgaProgramCode"
                  label="Program Code"
                  name={[...localNamePath, 'programCode']}
                  rules={[{ required: required || isDe3, message: ' ' }]}
                  $readOnly={readOnly || isPGAReadOnly}
                  $itemSize={theme.size.short}
                >
                  <Select
                    disabled={formDisabled || isDe3}
                    options={programCodes}
                    onChange={clearFields({
                      form,
                      fullNamePath,
                      exclude: [
                        'countryCode',
                        'fdaProductCode',
                        'agencyCode',
                        'programCode',
                        'disclaim',
                        'disclaimerCode',
                      ],
                    })}
                    showSearch
                    allowClear
                    filterOption={selectFilter}
                    notFoundContent={`None Found`}
                    dropdownMatchSelectWidth={false}
                  />
                </FormItem>
              )}
          </>

          {!disclaim &&
            getProcessingCodes().length > 0 &&
            agencyCode !== 'CPS' && (
              <FormItem
                label="Processing Code"
                name={[...localNamePath, 'processingCode']}
                rules={[{ required, message: ' ' }]}
                $readOnly={readOnly || isPGAReadOnly}
                $itemSize={theme.size.short}
              >
                <Select
                  disabled={formDisabled}
                  onChange={clearFields({
                    form,
                    fullNamePath,
                    exclude: [
                      'source',
                      'fdaProductCode',
                      'agencyCode',
                      'processingCode',
                      'programCode',
                      'disclaim',
                      'disclaimerCode',
                    ],
                  })}
                  showSearch
                  allowClear
                  filterOption={selectFilter}
                  notFoundContent={`None Found`}
                  dropdownMatchSelectWidth={false}
                >
                  {getProcessingCodes()}
                </Select>
              </FormItem>
            )}

          <>
            {flag.required === 'M' && disclaim && agencyCode !== 'CPS' && (
              <DisclaimerSelect
                disclaimerCodes={disclaimerCodes}
                localNamePath={[...localNamePath, 'disclaimerCode']}
                required={disclaim || isPGARequired || isDe3}
                disabled={formDisabled || isDe3}
                readOnly={readOnly || isPGAReadOnly}
                filter={filterDisclaimerCodes}
              />
            )}
          </>

          {/* TODO: disclaim field is not in schema, need to add a transform */}

          <>
            {/* AQ2 does not allow disclaiming. Check Section II – Disclaim and HTS Flagging Guidance (https://www.cbp.gov/sites/default/files/assets/documents/2020-May/APHIS%20Supplemental%20Trade%20Guide%20-%20Appendix%20APH-A%20%28ver.%202.0%29.pdf) */}
            {flag.required === 'M' &&
              pgaFlag !== UsPgaFlag.AQ2 &&
              agencyCode !== 'CPS' &&
              (!readOnly || disclaim) && (
                <Checkbox
                  form={form}
                  fullNamePath={[...localNamePath, 'disclaim']}
                  $spaceTop
                  $removeSpaceBottom
                  onChange={(value) => {
                    if (value && pgaFlag === 'DT1') {
                      // Highway Traffic Safety Administration only allows OFF code if disclaiming
                      setFields([
                        {
                          name: [...fullNamePath, 'programCode'],
                          value: 'OFF',
                        },
                      ]);
                    }
                    clearFields({
                      form,
                      fullNamePath,
                      exclude: [
                        'agencyCode',
                        'processingCode',
                        'programCode',
                        'disclaim',
                      ],
                    })();
                  }}
                  disabled={formDisabled || isDe3}
                  text="Disclaim?"
                />
              )}
          </>
        </StyledFlexDiv>
        {[UsPgaFlag.AQ1, UsPgaFlag.AQ2].includes(pgaFlag) && (
          <StyledFlexDiv>
            <IntendedUseSelect
              disabled={formDisabled}
              readOnly={readOnly || isPGAReadOnly}
              form={form}
              required={!isDisclaimed && (required || isPGARequired)}
              localNamePath={[...localNamePath, 'intendedUse']}
              fullNamePath={[...fullNamePath, 'intendedUse']}
              codes={intendedUseCodes}
            />

            {
              // Intended Use Description should present for 980.000(For Other Use)
              intendedUseCode === '980.000' && (
                <FormItem
                  name={[...localNamePath, 'intendedUseDescription']}
                  label="Intended Use Description"
                  $itemSize={theme.size.short}
                  $readOnly={readOnly}
                  $inline
                  debounce
                >
                  <Input.TextArea
                    autoSize
                    disabled={formDisabled}
                    maxLength={21}
                  />
                </FormItem>
              )
            }
          </StyledFlexDiv>
        )}
      </StyledFormInlineDiv>
      {(!disclaim || agencyCode === 'CPS') &&
        (agencyCode === 'FDA' ? (
          <FdaForm mot={mot} {...pgaProps} />
        ) : agencyCode === 'APH' ? (
          <AphForm {...pgaProps} />
        ) : agencyCode === 'TTB' ? (
          <TtbForm {...pgaProps} />
        ) : agencyCode === 'EPA' ? (
          <EpaForm {...pgaProps} />
        ) : agencyCode === 'CPS' ? (
          <CpscForm {...pgaProps} />
        ) : (
          agencyCode === 'NHT' && <NhtForm {...pgaProps} />
        ))}
    </>
  );
};

export default memo(PgaForm);
